import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { playClip, spawnFx, spawnProjectile, type ProjectileSpec } from '@src/ecs/archetypes';
import { ClipId, Dying, Enemy, EnemyKind, Player, Position, Sprite, SpriteId, Team, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { isWallEnemy, wallInward } from '@src/game/WallEnemies';
import { WeaponId } from '@src/game/weapons/WeaponDefs';

const MODE_IDLE = 0;
const MODE_WIND = 1;
const MOUTH = 0.55;
const GARGOYLE_TINT = 0xffc14a;
const SPITTER_TINT = 0x8cff3a;

/** The slice of wall-gun config both kinds share. */
interface WallGunConfig {
  telegraph: number;
  fireInterval: number;
  boltSpeed: number;
  boltDamage: number;
  boltLife: number;
  boltRadius: number;
  knockback: number;
}

function wallConfig(kind: number): WallGunConfig {
  return kind === EnemyKind.Spitter ? Config.enemies.spitter : Config.enemies.gargoyle;
}

/**
 * Stationary wall guns. They hold their mount, face the nearest wizard, telegraph
 * a cast, then spit. Gargoyles fire one aimed bolt; spitters fire a short acid fan
 * that punishes standing still. Position is pinned every tick so knockback and
 * swarm unstack cannot peel them off the stone.
 */
export class WallEnemySystem implements GameSystem {
  private ctx!: GameContext;
  private readonly bolt: ProjectileSpec = {
    owner: -1,
    weapon: WeaponId.Wand,
    sprite: SpriteId.BoltWand,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    radius: Config.enemies.gargoyle.boltRadius,
    life: Config.enemies.gargoyle.boltLife,
    damage: Config.enemies.gargoyle.boltDamage,
    spawnerMult: 0,
    knockback: Config.enemies.gargoyle.knockback,
    pierce: 0,
    tint: GARGOYLE_TINT,
    scale: 0.85,
    grow: 0,
    team: Team.Enemy,
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const enemies = query(ctx.world, [Enemy, Position, Sprite]);
    const players = query(ctx.world, [Player, Position]);
    const fighting = ctx.room.phase === RoomPhase.Fight;

    for (let i = 0; i < enemies.length; i++) {
      const eid = enemies[i];
      const kind = Enemy.kind[eid];
      if (!isWallEnemy(kind)) continue;
      Velocity.x[eid] = 0;
      Velocity.y[eid] = 0;
      if (Dying.flag[eid]) continue;
      const c = wallConfig(kind);

      let tx = 0;
      let ty = 0;
      let best = Infinity;
      for (let p = 0; p < players.length; p++) {
        const pid = players[p];
        if (Player.dead[pid]) continue;
        const dx = Position.x[pid] - Position.x[eid];
        const dy = Position.y[pid] - Position.y[eid];
        const d2 = dx * dx + dy * dy;
        if (d2 >= best) continue;
        best = d2;
        tx = dx;
        ty = dy;
      }

      if (best < Infinity && Math.abs(tx) > 0.05) Sprite.face[eid] = tx < 0 ? -1 : 1;
      const inward = wallInward(Position.x[eid], Position.y[eid]);
      Sprite.rot[eid] = Math.sin(ctx.time * 3 + Enemy.seed[eid] * 8) * 0.04;

      if (!fighting || best === Infinity) {
        Enemy.mode[eid] = MODE_IDLE;
        continue;
      }

      Enemy.beat[eid] -= dt;
      if (Enemy.mode[eid] === MODE_WIND) {
        const u = 1 - Math.max(0, Enemy.beat[eid]) / c.telegraph;
        Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.35 + u * 0.55);
        Sprite.squash[eid] = -0.2 - u * 0.25;
        if (Enemy.beat[eid] > 0) continue;
        this.fire(eid, kind, c, tx, ty, inward.nx, inward.ny);
        Enemy.mode[eid] = MODE_IDLE;
        Enemy.beat[eid] = c.fireInterval * (0.82 + Enemy.seed[eid] * 0.36);
        continue;
      }

      if (Enemy.beat[eid] > 0) continue;
      Enemy.mode[eid] = MODE_WIND;
      Enemy.beat[eid] = c.telegraph;
      playClip(eid, ClipId.Cast, true, true);
      Sprite.flash[eid] = 0.55;
      Sprite.squash[eid] = -0.28;
    }
  }

  private fire(eid: number, kind: number, c: WallGunConfig, tx: number, ty: number, inx: number, iny: number): void {
    const ctx = this.ctx;
    const spitter = kind === EnemyKind.Spitter;
    const tint = spitter ? SPITTER_TINT : GARGOYLE_TINT;
    const len = Math.hypot(tx, ty) || 1;
    const base = Math.atan2(ty / len, tx / len);
    const x = Position.x[eid] + inx * MOUTH + Math.cos(base) * 0.12;
    const y = Position.y[eid] + iny * MOUTH + Math.sin(base) * 0.12;
    const speed = c.boltSpeed * difficulty().projectileSpeed;

    const s = Config.enemies.spitter;
    const shots = spitter ? s.fanCount : 1;
    const spread = spitter ? (s.fanDegrees * Math.PI) / 180 : 0;
    this.bolt.owner = eid;
    this.bolt.radius = c.boltRadius;
    this.bolt.life = c.boltLife;
    this.bolt.damage = c.boltDamage;
    this.bolt.knockback = c.knockback;
    this.bolt.tint = tint;
    this.bolt.scale = spitter ? 0.7 : 0.85;
    for (let i = 0; i < shots; i++) {
      const t = shots > 1 ? i / (shots - 1) - 0.5 : 0;
      const a = base + t * spread;
      this.bolt.x = x;
      this.bolt.y = y;
      this.bolt.vx = Math.cos(a) * speed;
      this.bolt.vy = Math.sin(a) * speed;
      spawnProjectile(ctx, this.bolt);
    }
    spawnFx(ctx, SpriteId.MuzzleWand, x, y, base, 0.85, tint, 0.82);
    emitParticles(ctx.fx, spitter ? ParticleKind.ImpGoo : ParticleKind.MuzzleSparks, x, y, Math.cos(base), Math.sin(base), 0.7, tint);
    playClip(eid, ClipId.Cast, true, false);
    Sprite.squash[eid] = 0.35;
  }
}
